import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/StatusBits";
import { QuestionList, askQuestions, toQA } from "@/components/AiQuestions";
import { api, errMsg } from "@/lib/api";

const START = { kind: "ai", purpose: "", method: "", goal: "", tone: "professional", objective_type: "followup",
  frequency: { retry_interval_minutes: 240, max_attempts_per_day: 2, max_total_attempts: 6 } };

const Pick = ({ value, onChange, items, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="bg-white h-9 text-xs"><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const Brief = ({ f, setF }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const freq = (k) => (e) => setF({ ...f, frequency: { ...f.frequency, [k]: e.target.value } });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Type of call"><Pick testId="builder-kind" value={f.kind} onChange={set("kind")} items={[["ai", "AI phone call (live conversation)"], ["message", "Repeating recorded message"]]} /></Field>
        <Field label="Tone"><Pick testId="builder-tone" value={f.tone} onChange={set("tone")} items={[["friendly", "Friendly"], ["professional", "Professional"], ["firm", "Firm"]]} /></Field>
      </div>
      <Field label="What is this blowup for?"><Textarea rows={2} data-testid="builder-purpose" value={f.purpose} onChange={set("purpose")} placeholder="e.g. Remind approved merchants their offer expires Friday" /></Field>
      <Field label="How should it be done?"><Textarea rows={2} data-testid="builder-method" value={f.method} onChange={set("method")} placeholder="e.g. Short and friendly, mention the amount, offer to connect them to an agent" /></Field>
      <Field label="Goal"><Input data-testid="builder-goal" value={f.goal} onChange={set("goal")} placeholder="e.g. Get them on the phone with an agent before Friday" /></Field>
      <div>
        <p className="text-xs font-medium text-slate-600 mb-1">How often to call</p>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Retry every (hours)"><Input type="number" min="1" data-testid="builder-retry-hours" value={Math.round((f.frequency.retry_interval_minutes || 0) / 60)} onChange={(e) => setF({ ...f, frequency: { ...f.frequency, retry_interval_minutes: Number(e.target.value) * 60 } })} /></Field>
          <Field label="Max per day"><Input type="number" min="1" data-testid="builder-max-day" value={f.frequency.max_attempts_per_day} onChange={freq("max_attempts_per_day")} /></Field>
          <Field label="Max total"><Input type="number" min="1" data-testid="builder-max-total" value={f.frequency.max_total_attempts} onChange={freq("max_total_attempts")} /></Field>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">These settings are used when the queue repeats calls. Pressing Call always dials right away.</p>
      </div>
    </div>
  );
};

const Draft = ({ d, setD }) => {
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <div className="space-y-3" data-testid="builder-draft">
      <Field label="Script name"><Input data-testid="builder-name" value={d.name} onChange={set("name")} /></Field>
      {d.kind === "message" ? (
        <>
          <Field label="Recorded message (spoken in the selected voice)" hint="Callers hear: press 1 to talk to someone, press 9 to stop calls. Left as voicemail when a machine answers.">
            <Textarea rows={6} data-testid="builder-message-text" value={d.message_text} onChange={set("message_text")} />
          </Field>
          {/\[[^\]]+\]/.test(d.message_text || "") && <p className="text-xs text-amber-700" data-testid="builder-placeholder-warning">Replace the [bracketed] text — it would be read out loud.</p>}
          <p className="text-xs text-slate-500" data-testid="builder-repeat-note">The message repeats non-stop until the person hangs up.</p>
        </>
      ) : (
        <>
          <Field label="Opening line"><Input data-testid="builder-opening" value={d.opening_line} onChange={set("opening_line")} /></Field>
          <Field label="Call flow / talking points"><Textarea rows={6} data-testid="builder-body" value={d.body} onChange={set("body")} /></Field>
          <Field label="Special instructions"><Textarea rows={3} data-testid="builder-special" value={d.special_instructions} onChange={set("special_instructions")} /></Field>
          <Field label="Callback / follow-up"><Textarea rows={2} data-testid="builder-callback" value={d.callback_instructions} onChange={set("callback_instructions")} /></Field>
        </>
      )}
    </div>
  );
};

export const ScriptBuilder = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(START);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qs, setQs] = useState(null);
  const [answers, setAnswers] = useState({});
  const close = (o) => {
    if (!o) { setDraft(null); setQs(null); }
    onOpenChange(o);
  };
  const next = async () => {
    setBusy(true);
    try {
      const d = await askQuestions(f);
      if (d.ready) await generate([]);
      else { setQs(d.questions); setAnswers({}); }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const generate = async (qa) => {
    setBusy(true);
    try {
      setDraft((await api.post("/scripts/generate", { ...f, answers: qa }, { timeout: 120000 })).data);
      setQs(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    try {
      const { data } = await api.post("/scripts", draft);
      toast.success("Script saved and selected");
      onSaved?.(data);
      close(false);
      setF(START);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="script-builder">
        <DialogHeader><DialogTitle>Build a script</DialogTitle></DialogHeader>
        {!draft && qs ? (
          <>
            <QuestionList questions={qs} answers={answers} setAnswers={setAnswers} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" data-testid="builder-questions-back-btn" onClick={() => setQs(null)}>Back</Button>
              <Button data-testid="builder-generate-btn" disabled={busy} onClick={() => generate(toQA(qs, answers))}>
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{busy ? "Writing your script…" : "Write the full script"}
              </Button>
            </div>
          </>
        ) : !draft ? (
          <>
            <Brief f={f} setF={setF} />
            <Button data-testid="builder-next-btn" disabled={busy || !f.purpose.trim()} onClick={next}>
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{busy ? "Working…" : "Next: AI asks what it needs"}
            </Button>
          </>
        ) : (
          <>
            <Draft d={draft} setD={setDraft} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" data-testid="builder-back-btn" onClick={() => setDraft(null)}>Back</Button>
              <Button data-testid="builder-save-btn" onClick={save}>Save & use</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
